// paex-occasion-badge.js — reveals the card/cart occasion badges (Store
// Availability UX v2, design 1g echo). Requires paex-store-core.js +
// paex-edd.js first. Re-execution-safe: many cards emit the same script tags;
// the window guard makes repeats no-ops (files come from cache).
//
// A badge shows ONLY when: destination region is known (typed postal/city →
// geo-IP) AND the transit-table estimate arrives on or before the arrive-by
// date. Unknown destination = badge stays hidden — never a promise on a guess.

if (!window.paexOccasionBadge) {
  window.paexOccasionBadge = true;

  (function () {
    'use strict';

    // self-contained session readers (no paex-store-core dependency on
    // collection pages — perf review): same keys, same shapes, guarded.
    function readSession(key) {
      try {
        var raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    }

    function region() {
      var dest = readSession('paex:dest');
      if (dest && dest.region) return String(dest.region).trim().toUpperCase();
      var geo = window.paexGeo || readSession('paex:geo');
      if (geo && geo.regionCode) return String(geo.regionCode).trim().toUpperCase();
      return null;
    }

    // Halloween clock - PE (2026-10-09): while the clock runs, the verdict is
    // paexClock's (same engine + arrive-by as the cart box; remote, US and
    // unknown destinations never get a green badge). Cards: green or hidden.
    // Cart-page lines (.cart-item): green on time, amber chip_late when late,
    // hidden when unknown. Cards AND cart lines judge on the same data as
    // the PDP / cart box: the live date when the destination has one. A
    // badge whose processing equals the theme default asks with processing
    // null — the clock's own key, so it shares the memo the PDP, the cart
    // box and the clock state already filled (no extra fetch); every other
    // distinct value costs at most one live read per destination (memoized
    // in paexClock). One estimate per distinct processing value per scan.
    var clockRun = 0;

    function clockScan(badges) {
      var clock = window.paexClock;
      var run = ++clockRun;
      var buckets = Object.create(null);
      var base = parseInt(clock.config && clock.config.edd && clock.config.edd.processing, 10);
      if (!(base >= 0)) base = 0;
      if (base > 30) base = 30;
      badges.forEach(function (badge) {
        var processing = parseInt(badge.dataset.processing, 10);
        if (!(processing >= 0)) processing = 0;
        if (processing > 30) processing = 30;
        var cartLine = Boolean(badge.closest('.cart-item'));
        var shared = processing === base;
        var bucket = shared ? 'default' : String(processing);
        if (!(bucket in buckets)) {
          buckets[bucket] = clock.estimate({ processing: shared ? null : processing })
            .catch(function () { return null; });
        }
        buckets[bucket].then(function (result) {
          if (run !== clockRun) return; // a newer scan owns the badges
          var off = !clock.state || clock.state.phase === 'off';
          var verdict = off || !result ? 'unknown' : result.verdict;
          var late = cartLine && verdict === 'late';
          var green = verdict === 'ontime' || verdict === 'lastday';
          var text = late ? clock.t('chip_late') : badge.dataset.label;
          if ((green || late) && badge.textContent !== text) badge.textContent = text;
          badge.classList.toggle('paex-occasion-badge--late', late);
          badge.hidden = !(green || late);
        });
      });
    }

    function scan() {
      var badges = document.querySelectorAll('[data-paex-occasion]');
      if (!badges.length) return;
      if (window.paexClock) {
        clockScan(badges);
        return;
      }
      // the clock is on this page but not booted yet: wait for
      // paex:clock-ready (hidden meanwhile — never a promise on a guess)
      if (document.getElementById('PaexClockConfig')) return;
      var dest = region();
      if (!dest || typeof paexEddEstimate !== 'function') {
        badges.forEach(function (badge) { badge.hidden = true; });
        return;
      }
      // every placeholder carries the identical dispatch config; only the
      // per-product processing days differ — compute ONCE per distinct value
      // (50-card grids: a handful of estimates, not fifty — perf review)
      var first = badges[0];
      var estimates = Object.create(null); // null prototype: any key is safe
      badges.forEach(function (badge) {
        // normalize exactly like the engine (parseInt, 0–30) so "3", "3.0"
        // and junk share one bucket
        var processing = parseInt(badge.dataset.processing, 10);
        if (!(processing >= 0)) processing = 0;
        if (processing > 30) processing = 30;
        var bucket = String(processing);
        if (!(bucket in estimates)) {
          estimates[bucket] = paexEddEstimate(dest, {
            cutoff: first.dataset.cutoff,
            tz: first.dataset.tz,
            days: first.dataset.days,
            holidays: first.dataset.holidays,
            transit: first.dataset.transit,
            processing: processing,
          });
        }
        var estimate = estimates[bucket];
        var green = estimate && estimate.arrivalKey <= badge.dataset.arriveBy;
        if (green) {
          if (badge.textContent !== badge.dataset.label) badge.textContent = badge.dataset.label;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      });
    }

    function ready(callback) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
      } else {
        callback();
      }
    }

    ready(function () {
      scan();
      document.addEventListener('paex:geo-ready', scan);
      document.addEventListener('paex:dest-change', scan);
      // Halloween clock - PE (2026-10-09): verdict changes (destination,
      // cutoff, day rollover) repaint the badges
      document.addEventListener('paex:clock-ready', scan);
      document.addEventListener('paex:clock-change', scan);
      // cart drawer re-renders its HTML on every cart change — new placeholders
      // need a rescan (fetched HTML never re-executes script tags).
      // typeof-gate (W3 audit 2026-08-01): window.webvista is never set —
      // global.js's webvista is a top-level const. See paex-quick-add.js.
      if (typeof webvista !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && typeof webvista.subscribe === 'function') {
        webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          setTimeout(scan, 50);
        });
      }
    });
  })();
}
